import classNames from 'classnames';
import { VolumeTypeUtils } from 'infra/helpers/volumeTypeUtils';
import * as R from 'ramda';
import React from 'react';

import { Environment } from 'Environment';
import { connectFormikContext } from 'reactApp/components/Forms/ConnectFormikContext';
import { FieldCheckbox } from 'reactApp/components/Forms/FieldCheckbox';
import { FieldDependentValue } from 'reactApp/components/Forms/FieldDependentValue';
import { FieldInput } from 'reactApp/components/Forms/FieldInput';
import { FieldRadioGroup } from 'reactApp/components/Forms/FieldRadioGroup';
import { getIn } from 'reactApp/components/Forms/formUtils';
import { Link } from 'reactApp/components/Link/Link';
import { QuestionTooltip } from 'reactApp/components/questionTooltip/questionTooltip';
import { FieldVolumeType } from 'reactComponents/Forms/FieldVolumeType';
import { InfoMessage } from 'reactComponents/InfoMessage/InfoMessage';

import css from './FieldDiskTypeByAvailabilityZone.css';

@connectFormikContext()
export class FieldDiskTypeByAvailabilityZone extends React.Component {
    static defaultProps = {
        name: 'name',
    };

    _generateDiskReplicationType = (values) => {
        const {
            volumeTypes,
            _formikContext,
            physicalDiskFieldName,
            geoReplicationFieldName,
            availabilityZoneNameFieldName,
        } = this.props;

        return VolumeTypeUtils.getReplicationDiskType({
            type: getIn(values, physicalDiskFieldName),
            availabilityZoneName: getIn(
                _formikContext.values,
                availabilityZoneNameFieldName,
            ),
            isGeoReplication:
                values.physicalDisk === 'ssd'
                    ? false
                    : getIn(values, geoReplicationFieldName),
            volumeTypes,
        });
    };

    _renderGeoReplication() {
        const {
            _formikContext,
            physicalDiskFieldName,
            geoReplicationFieldName,
            hidden,
            hideGeoReplication,
            isDialog,
            infoWrapperClassName = '',
        } = this.props;

        const isHighIops =
            getIn(_formikContext.values, physicalDiskFieldName) === 'highIops';
        const isSsd =
            getIn(_formikContext.values, physicalDiskFieldName) === 'ssd';

        if (hideGeoReplication || isSsd) {
            return;
        }

        if (isHighIops) {
            return (
                <InfoMessage
                    className={classNames(
                        css.infoWrapper,
                        infoWrapperClassName,
                        {
                            [css.inWizard]: !isDialog,
                        },
                    )}
                >
                    Если необходима репликация, то ее нужно настроить на уровне
                    приложения.
                </InfoMessage>
            );
        }
        return (
            <FieldCheckbox
                dataTest="checkbox-use-geo-replication"
                controlLabel="Использовать георепликацию"
                name={geoReplicationFieldName}
                wrapperClassName={css.geoReplication}
                extraLabel={this._renderQuestionTooltip()}
                hidden={hidden}
            />
        );
    }

    _renderQuestionTooltip = () => {
        return (
            <QuestionTooltip
                position="right center"
                triggerDataTest="georeplication-tooltip"
            >
                Георепликация гарантирует хранение трех копий данных в двух
                датацентрах.{' '}
                <Link
                    href="https://mcs.mail.ru/help/disks-and-images/volume"
                    dataTest="georeplication-help-link"
                >
                    Дополнительная информация.
                </Link>
            </QuestionTooltip>
        );
    };

    _renderPriceTooltip = (prices) => {
        const { excludedDiskTypes } = this.props;
        return (
            <QuestionTooltip
                position="right center"
                triggerDataTest="disk-type-tooltip"
            >
                {VolumeTypeUtils.getDiskDescription(
                    prices,
                    excludedDiskTypes,
                ).map((item, index) => (
                    <div
                        key={index}
                        className={css.diskInfo}
                        dangerouslySetInnerHTML={{ __html: item }}
                    />
                ))}
            </QuestionTooltip>
        );
    };

    _renderRadioGroup = () => {
        const {
            prices,
            fieldClassName,
            name,
            diskTypeLabel,
            physicalDiskFieldName,
            geoReplicationFieldName,
            availabilityZoneNameFieldName,
            validateOnGeneratedValueChange,
            excludedDiskTypes,
            hidden,
        } = this.props;

        return (
            <>
                <FieldRadioGroup
                    {...{
                        options: VolumeTypeUtils.getDiskTypeOptions(
                            excludedDiskTypes,
                        ),
                        label: diskTypeLabel || 'Тип диска',
                        name: physicalDiskFieldName,
                        fieldClassName,
                        hidden,
                        type: 'inline',
                        extraLabel: this._renderPriceTooltip(prices),
                    }}
                />

                {this._renderGeoReplication()}

                <FieldDependentValue
                    {...{
                        name,
                        fieldControl: FieldInput,
                        hidden: true,
                        dependentFieldNames: [
                            physicalDiskFieldName,
                            geoReplicationFieldName,
                            availabilityZoneNameFieldName,
                        ],
                        generateValue: this._generateDiskReplicationType,
                        validateOnGeneratedValueChange,
                    }}
                />
            </>
        );
    };

    _renderSelect = () => {
        const { name, volumeTypes, prices, _formikContext } = this.props;
        const availabilityZoneName = R.path(
            ['values', 'availabilityZoneName'],
            _formikContext,
        );

        return (
            <FieldDependentValue
                {...{
                    fieldControl: FieldVolumeType,
                    name,
                    label: 'Тип диска',
                    volumeTypes: VolumeTypeUtils.getOptionsForSelect({
                        volumeTypes,
                        availabilityZoneName,
                    }),
                    prices,
                    dependentFieldNames: Environment.isProductionHost
                        ? ['availabilityZoneName']
                        : [],
                    generateValue: this._getDiskTypeForSelect,
                }}
            />
        );
    };

    _getDiskTypeForSelect = () => {
        const { _formikContext, volumeTypes } = this.props;

        const availabilityZoneName = R.path(
            ['values', 'availabilityZoneName'],
            _formikContext,
        );

        return (
            VolumeTypeUtils.getDiskTypeForSelect({
                availabilityZoneName,
                physicalDisk: _formikContext.values.physicalDisk,
            }) || volumeTypes[0]?.name
        );
    };

    render() {
        return VolumeTypeUtils.getIsControlTypeDropdown(
            this.props.volumeTypes.map((item) => item.name),
        )
            ? this._renderSelect()
            : this._renderRadioGroup();
    }
}
